import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Calendar, FileText, ArrowLeft } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';

interface Booking {
  id: string;
  title: string;
  booking_number: number | null;
  start_date: string;
  start_time: string | null;
  total_amount: number | null;
  customer: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
  status: string;
  services?: Array<{
    name: string;
    vehicle?: {
      make?: string;
      model?: string;
    };
  }>;
}

interface Project {
  id: string;
  title: string;
  status: string;
  customer_id: string | null;
  customer?: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
}

interface SelectBookingOrProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectBooking?: (booking: Booking) => void;
  onSelectProject?: (project: Project) => void;
  businessId: string;
  hasCalendarApp: boolean;
  hasProjectsApp: boolean;
}

type ModalView = 'options' | 'bookings' | 'projects';

export default function SelectBookingOrProjectModal({
  isOpen,
  onClose,
  onSelectBooking,
  onSelectProject,
  businessId,
  hasCalendarApp,
  hasProjectsApp
}: SelectBookingOrProjectModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [modalView, setModalView] = useState<ModalView>('options');
  const [searchQuery, setSearchQuery] = useState('');
  const [bookingsTab, setBookingsTab] = useState<'current' | 'completed'>('current');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setModalView('options');
      setSearchQuery('');
      setBookingsTab('current');
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (modalView === 'bookings' && hasCalendarApp) {
      fetchBookings();
    } else if (modalView === 'projects' && hasProjectsApp) {
      fetchProjects();
    }
  }, [modalView, hasCalendarApp, hasProjectsApp, businessId]);

  const fetchBookings = async () => {
    if (!businessId) return;
    
    setLoadingBookings(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('bookings')
        .select(`
          id,
          title,
          booking_number,
          start_date,
          start_time,
          total_amount,
          status,
          customer:customers!bookings_customer_id_fkey(
            id,
            first_name,
            last_name
          ),
          services:booking_services(
            name,
            vehicle:customer_vehicles(
              make,
              model
            )
          )
        `)
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .order('start_date', { ascending: false })
        .order('start_time', { ascending: false })
        .limit(100);

      if (fetchError) throw fetchError;
      setBookings((data || []) as Booking[]);
    } catch (err: any) {
      console.error('Error fetching bookings:', err);
      setError(err.message || 'Failed to load bookings');
    } finally {
      setLoadingBookings(false);
    }
  };

  const fetchProjects = async () => {
    if (!businessId) return;
    
    setLoadingProjects(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('projects')
        .select(`
          id,
          title,
          status,
          customer_id,
          customer:customers!projects_customer_id_fkey(
            id,
            first_name,
            last_name
          )
        `)
        .eq('business_id', businessId)
        .eq('is_deleted', false)
        .eq('project_status', 'open')
        .order('created_at', { ascending: false })
        .limit(100);

      if (fetchError) throw fetchError;
      setProjects((data || []) as Project[]);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      setError(err.message || 'Failed to load projects');
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
      setModalView('options');
      setSearchQuery('');
    }, 200);
  };

  // Filter and sort bookings based on tab and search
  const filteredBookings = (() => {
    let filtered = bookings;
    
    // If searching, show all bookings that match
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = bookings.filter(booking => 
        booking.title?.toLowerCase().includes(query) ||
        booking.booking_number?.toString().includes(query) ||
        booking.customer?.first_name?.toLowerCase().includes(query) ||
        booking.customer?.last_name?.toLowerCase().includes(query) ||
        booking.services?.some(s => s.name?.toLowerCase().includes(query))
      );
    } else {
      // Filter by tab when not searching
      if (bookingsTab === 'current') {
        // Current tab: in_progress and scheduled
        filtered = bookings.filter(booking => 
          booking.status === 'in_progress' || booking.status === 'scheduled'
        );
        // Sort: in_progress first, then scheduled
        filtered.sort((a, b) => {
          if (a.status === 'in_progress' && b.status !== 'in_progress') return -1;
          if (a.status !== 'in_progress' && b.status === 'in_progress') return 1;
          return 0;
        });
      } else if (bookingsTab === 'completed') {
        // Completed tab: only completed
        filtered = bookings.filter(booking => booking.status === 'completed');
      }
    }
    
    return filtered;
  })();

  const filteredProjects = projects.filter(project => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      project.title?.toLowerCase().includes(query) ||
      project.customer?.first_name?.toLowerCase().includes(query) ||
      project.customer?.last_name?.toLowerCase().includes(query)
    );
  });

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100070,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={handleClose}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ zIndex: 100071 }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className={`bg-gray-50 border-b border-gray-200 ${isMobile ? 'px-4 py-3' : 'px-6 py-4'} flex items-center justify-between sticky top-0 z-10`}>
            <div className="flex items-center space-x-3">
              {modalView !== 'options' && (
                <button
                  onClick={() => {
                    setModalView('options');
                    setSearchQuery('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              )}
              <h2 className="text-lg font-semibold text-gray-900">
                {modalView === 'options' 
                  ? 'Assign Expense'
                  : modalView === 'bookings'
                  ? 'Select Booking'
                  : 'Select Project'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {modalView === 'options' ? (
              <div className={`${isMobile ? 'p-3' : 'p-6'} space-y-3`}>
                {/* Bookings Option */}
                {hasCalendarApp && (
                  <button
                    type="button"
                    onClick={() => {
                      setModalView('bookings');
                      fetchBookings();
                    }}
                    className="w-full px-4 py-3 text-left bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-150"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Calendar className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900">Bookings</div>
                        <div className="text-xs text-gray-500 mt-0.5">Select a booking</div>
                      </div>
                    </div>
                  </button>
                )}

                {/* Projects Option */}
                {hasProjectsApp && (
                  <button
                    type="button"
                    onClick={() => {
                      setModalView('projects');
                      fetchProjects();
                    }}
                    className="w-full px-4 py-3 text-left bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-150"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900">Projects</div>
                        <div className="text-xs text-gray-500 mt-0.5">Select a project</div>
                      </div>
                    </div>
                  </button>
                )}
              </div>
            ) : modalView === 'bookings' ? (
              <div className={`${isMobile ? 'p-3' : 'p-6'}`}>
                {/* Search */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search bookings..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Tabs - Only show when not searching */}
                {!searchQuery && (
                  <div className="mb-4 flex space-x-2 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setBookingsTab('current');
                        setSearchQuery('');
                      }}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        bookingsTab === 'current'
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Current
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setBookingsTab('completed');
                        setSearchQuery('');
                      }}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        bookingsTab === 'completed'
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Completed
                    </button>
                  </div>
                )}

                {/* Bookings List */}
                {loadingBookings ? (
                  <div className="text-center py-8 text-gray-500 text-sm">Loading bookings...</div>
                ) : error ? (
                  <div className="text-center py-8 text-red-500 text-sm">{error}</div>
                ) : filteredBookings.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    {searchQuery ? 'No bookings found' : 'No bookings available'}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredBookings.map((booking) => {
                      const customerInitials = booking.customer
                        ? `${booking.customer.first_name?.[0] || ''}${booking.customer.last_name?.[0] || ''}`
                        : '?';

                      const statusBadge = (
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${
                          booking.status === 'completed' ? 'bg-green-100 text-green-700' :
                          booking.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                          booking.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                          booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1).replace('_', ' ')}
                        </span>
                      );

                      return (
                        <button
                          key={booking.id}
                          type="button"
                          onClick={() => {
                            if (onSelectBooking) {
                              onSelectBooking(booking);
                            }
                            handleClose();
                          }}
                          className="w-full text-left bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer flex overflow-hidden"
                        >
                          {/* Status Bar - Left Edge */}
                          <div className={`w-1 rounded-l-xl flex-shrink-0 ${
                            booking.status === 'completed' ? 'bg-green-500' :
                            booking.status === 'in_progress' ? 'bg-yellow-500' :
                            booking.status === 'scheduled' ? 'bg-blue-500' :
                            booking.status === 'cancelled' ? 'bg-red-500' :
                            'bg-gray-400'
                          }`} />
                          
                          {isMobile ? (
                            <div className="p-3 flex-1 min-w-0">
                              {/* Row 1: Avatar + Name + Booking # */}
                              <div className="flex items-center gap-2.5 mb-2">
                                <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                                  {customerInitials}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                                      {booking.customer 
                                        ? `${booking.customer.first_name} ${booking.customer.last_name}`
                                        : 'No Customer'}
                                    </h3>
                                    {booking.booking_number && (
                                      <span className="text-xs text-gray-400 flex-shrink-0">#{booking.booking_number}</span>
                                    )}
                                  </div>
                                  {booking.services && booking.services.length > 0 ? (
                                    <p className="text-xs text-gray-500 truncate mt-0.5">
                                      {booking.services[0].name}
                                      {booking.services[0].vehicle?.make && ` • ${booking.services[0].vehicle.make} ${booking.services[0].vehicle.model || ''}`}
                                      {(booking.services?.length || 0) > 1 && ` +${(booking.services?.length || 0) - 1} more`}
                                    </p>
                                  ) : (
                                    <p className="text-xs text-gray-500 truncate mt-0.5">{booking.title || 'Untitled Booking'}</p>
                                  )}
                                </div>
                              </div>
                              {/* Row 2: Status + Date + Amount */}
                              <div className="flex items-center justify-between gap-2 pl-0.5">
                                {statusBadge}
                                <div className="flex items-center gap-3 text-xs text-gray-500">
                                  {booking.start_date && (
                                    <span className="whitespace-nowrap">
                                      {new Date(booking.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      {booking.start_time && `, ${booking.start_time}`}
                                    </span>
                                  )}
                                  {booking.total_amount != null && (
                                    <span className="font-bold text-sm text-gray-900 whitespace-nowrap">
                                      ${Number(booking.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="p-4 flex-1 flex items-center justify-between min-w-0">
                              {/* Left Section - Customer Info */}
                              <div className="flex items-center space-x-4 flex-1 min-w-0">
                                <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                                  {customerInitials}
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-3 mb-1">
                                    <h3 className="text-base font-semibold text-gray-900 truncate">
                                      {booking.customer 
                                        ? `${booking.customer.first_name} ${booking.customer.last_name}`
                                        : 'No Customer'}
                                    </h3>
                                    {booking.booking_number && (
                                      <span className="text-xs text-gray-500 flex-shrink-0">#{booking.booking_number}</span>
                                    )}
                                    {statusBadge}
                                  </div>
                                  
                                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                                    {booking.services && booking.services.length > 0 ? (
                                      <div className="flex items-center space-x-2 min-w-0 overflow-hidden">
                                        <span className="font-medium truncate">{booking.services[0].name}</span>
                                        {booking.services[0].vehicle?.make && (
                                          <span className="text-gray-500 truncate">
                                            • {booking.services[0].vehicle.make} {booking.services[0].vehicle.model}
                                          </span>
                                        )}
                                        {(booking.services?.length || 0) > 1 && (
                                          <span className="text-gray-500 flex-shrink-0">
                                            +{(booking.services?.length || 0) - 1} more
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="text-sm text-gray-600 truncate min-w-0">
                                        {booking.title || 'Untitled Booking'}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Right Section - Date, Time, Amount */}
                              <div className="flex items-center space-x-6 flex-shrink-0">
                                {booking.start_date && (
                                  <div className="text-right flex-shrink-0">
                                    <p className="text-xs text-gray-500 whitespace-nowrap">Date</p>
                                    <p className="text-sm font-medium text-gray-900 whitespace-nowrap">
                                      {new Date(booking.start_date).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })}
                                    </p>
                                    {booking.start_time && (
                                      <p className="text-xs text-gray-500 mt-1 whitespace-nowrap">{booking.start_time}</p>
                                    )}
                                  </div>
                                )}
                                {booking.total_amount != null && (
                                  <div className="text-right flex-shrink-0">
                                    <p className="text-xs text-gray-500 whitespace-nowrap">Total</p>
                                    <p className="text-lg font-bold text-gray-900 whitespace-nowrap">
                                      ${Number(booking.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className={`${isMobile ? 'p-3' : 'p-6'}`}>
                {/* Search */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search projects..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Projects List */}
                {loadingProjects ? (
                  <div className="text-center py-8 text-gray-500 text-sm">Loading projects...</div>
                ) : error ? (
                  <div className="text-center py-8 text-red-500 text-sm">{error}</div>
                ) : filteredProjects.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    {searchQuery ? 'No projects found' : 'No projects available'}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredProjects.map((project) => {
                      const customerInitials = project.customer
                        ? `${project.customer.first_name?.[0] || ''}${project.customer.last_name?.[0] || ''}`
                        : '?';

                      const getProjectStatusColor = (status: string) => {
                        switch (status) {
                          case 'completed':
                            return 'bg-green-500';
                          case 'in_progress':
                            return 'bg-yellow-500';
                          case 'open':
                            return 'bg-blue-500';
                          case 'cancelled':
                            return 'bg-red-500';
                          default:
                            return 'bg-gray-400';
                        }
                      };

                      const getProjectStatusBadgeColor = (status: string) => {
                        switch (status) {
                          case 'completed':
                            return 'bg-green-100 text-green-700';
                          case 'in_progress':
                            return 'bg-yellow-100 text-yellow-700';
                          case 'open':
                            return 'bg-blue-100 text-blue-700';
                          case 'cancelled':
                            return 'bg-red-100 text-red-700';
                          default:
                            return 'bg-gray-100 text-gray-700';
                        }
                      };

                      return (
                        <button
                          key={project.id}
                          type="button"
                          onClick={() => {
                            if (onSelectProject) {
                              onSelectProject(project);
                            }
                            handleClose();
                          }}
                          className="w-full text-left bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer flex overflow-hidden"
                        >
                          {/* Status Bar - Left Edge */}
                          <div className={`w-1 rounded-l-xl flex-shrink-0 ${getProjectStatusColor(project.status || 'open')}`} />
                          
                          <div className="p-4 flex-1 flex items-center justify-between min-w-0">
                            {/* Left Section - Customer Info */}
                            <div className="flex items-center space-x-4 flex-1 min-w-0">
                              {/* Avatar */}
                              <div className="h-12 w-12 rounded-full bg-purple-500 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                                {customerInitials}
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-3 mb-1">
                                  <h3 className="text-base font-semibold text-gray-900 truncate">
                                    {project.title}
                                  </h3>
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${getProjectStatusBadgeColor(project.status || 'open')}`}>
                                    {project.status ? project.status.charAt(0).toUpperCase() + project.status.slice(1).replace('_', ' ') : 'Open'}
                                  </span>
                                </div>
                                
                                {/* Customer Name */}
                                {project.customer && (
                                  <div className="text-sm text-gray-600 truncate">
                                    {project.customer.first_name} {project.customer.last_name}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

